// src/worker/balances-calc.js
//
// Pure money maths for GET /api/balances, extracted from worker.js so it can be
// unit-tested (Rung ② — behavioural tests on the money paths) and so the Worker
// handler shrinks to "fetch rows → compute → return". This is a behaviour-
// preserving extraction: the logic and the output shape the frontend reads are
// unchanged.
//
// What it computes, per staff, for a year:
//   - holiday_used = count of the year's rota shifts of type 'holiday'
//   - sick_used    = count of the year's rota shifts of type 'sick'
//   - toil_balance = 8 × (work shifts that land on a public holiday IN THE
//                    STAFF MEMBER'S COUNTRY) − 8 × (TOIL days taken)
//
// Public holidays are COUNTRY-SCOPED: a UAE staffer accrues only for UAE PHs,
// a Qatar staffer only for Qatar PHs. If a staffer's country can't be resolved
// (no store→area→country join), work-on-PH accrual is skipped for them (their
// holiday/sick/toil counts still apply).
//
// Input rows are exactly the four datasets the handler fetches:
//   balanceRows : [{ staff_id, year, holiday_total, sick_total, updated_at }]
//   shiftRows   : [{ staff_id, week_start, day, type }]   (day = 'Mon'..'Sun')
//   phRows      : [{ date: 'YYYY-MM-DD', country }]
//   staffRows   : [{ id, store, stores: { area_id, areas: { country, region } } }]
//                 (PH accrual matches on areas.region — see staffCountry below)
// Output is one row per staff IN balanceRows (a staffer with shifts but no
// balance row is not returned — same as before).

// v129.22 — ANCHOR-AGNOSTIC. This was a fixed `{ Mon: 0, … Sun: 6 }` table
// added blindly to week_start, which silently assumed every week starts on a
// Monday. It doesn't: **Qatar rotas anchor the week on SUNDAY** and UAE on
// Monday (src/helpers/index.js:12 getWeekStart; worker.js:918 says so outright,
// and weekStartCandidates() in compliance-summary.js exists because of it).
//
// Confirmed against prod on 3 Aug 2026: ALL 2,088 Qatar shift rows carry a
// Sunday week_start — not one Monday. So every Qatar date this produced was
// wrong: Mon-Sat by one day, Sunday by six.
//
// What it cost, measured rather than assumed: over the 26-29 May Eid holiday,
// the shift missed the Tuesday but wrongly counted the Saturday, so 19 of the
// 21 staff who worked it happened to still net 4 days. Staff 83 and
// staff 97 did not work that Saturday and were short 8h each. That was
// luck, not correctness — a single-day Qatar holiday would have docked
// everyone who worked it.
//
// Deriving the offset from week_start's OWN weekday is correct for both
// anchors and needs no country lookup. A Monday-anchored week returns exactly
// what the old table did, so UAE is untouched BY CONSTRUCTION — the identity
// is test-pinned below.
const DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// Actual calendar date of a shift from week_start + day name. Noon UTC dodges
// any DST edge case — we only need the local date string back.
export function shiftDateStr(week_start, day) {
  const d = new Date(week_start + 'T12:00:00Z');
  const idx = DAY_INDEX[day];
  // Unknown/missing day name → week_start unchanged, matching the old
  // `|| 0` fallback rather than producing an Invalid Date.
  if (idx === undefined || isNaN(d.getTime())) return String(week_start);
  d.setUTCDate(d.getUTCDate() + ((idx - d.getUTCDay() + 7) % 7));
  return d.toISOString().slice(0, 10);
}

// The key used to match a staffer's area against public_holidays.country.
// PHs are stored as the BARE territory ("UAE"/"Qatar"); area.country is the
// ORG-PREFIXED label ("ELC - UAE"), so strip a leading "<ORG> - " prefix to
// recover the bare country. area.region is NOT reliable for this — in some data
// it is city-level ("Dubai"/"Manama") — so it is only a fallback when country is
// missing. Matching the org-prefixed label directly silently zeroed PH accrual
// for everyone (live bug 2026-06-11: 0/98 staff had any positive TOIL). Exported
// so the smoke canary asserts the exact key the pay maths uses.
export function phMatchKey(area) {
  if (!area) return null;
  const stripOrgPrefix = (s) =>
    (typeof s === 'string' ? s.replace(/^[^-]+-\s*/, '').trim() : null);
  return stripOrgPrefix(area.country) || (area.region || null) || null;
}

// v129.68 — variance TOIL is now COMPUTE-ON-READ (6 Aug audit, PAY HIGH).
// A work shift whose ≠8h variance a manager approved as TOIL carries
// variance_toil=true on the row; its signed (workedHrs − 8) is credited here
// on every read. The old model persisted a toil_adjust_delta from the client
// on EVERY save — no dedup marker existed, so reopening a 10h shift to fix
// the lunch and re-picking TOIL granted +2h again, forever. One row per shift
// (upsert on staff_id,week_start,day) + compute-on-read = idempotent by
// construction, the same reasoning that moved holiday/sick/toil days here in
// v80.16. Historical deltas already stored in toil_adjust stay honoured —
// old rows carry no flag, so nothing is double-counted.
//
// The maths deliberately mirrors the client modal (checkShiftVariance):
// shiftMins-with-overnight-wrap, minus lunch, minus 8, rounded to 0.1h PER
// SHIFT — so the figure the manager approved is the figure the balance moves by.
function shiftVarianceHours(sh) {
  const worked = shiftWorkedHours(sh);
  if (worked === null) return 0;   // missing/unparseable times: defensive no-op (unchanged)
  return Math.round((worked - 8) * 10) / 10;
}

// v129.154 (split shifts step 3, #1177) — a leg's WORKED hours, unrounded.
// Split out of shiftVarianceHours so a multi-leg day can sum its legs BEFORE
// the one 8h baseline is subtracted. Same maths as before: overnight wrap,
// minus lunch. Returns NULL for missing/unparseable times — callers must treat
// that leg as if it were not there (the old defensive rule: a flagged row with
// garbage times contributes NOTHING; it must never flag a day into a -8).
export function shiftWorkedHours(sh) {
  if (!sh || !sh.start_time || !sh.end_time) return null;
  const toMins = (t) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : NaN;
  };
  const sm = toMins(sh.start_time);
  const fm = toMins(sh.end_time);
  if (isNaN(sm) || isNaN(fm)) return null;
  const onSite = fm < sm ? fm + 1440 - sm : fm - sm;   // overnight wraps to next day
  return (onSite - (Number(sh.lunch_mins) || 0)) / 60;
}

export function computeYearBalances({ balanceRows, shiftRows, phRows, staffRows, year } = {}) {
  // Calendar-year bounds for date-gating shifts. A shift is counted by its ACTUAL
  // date (week_start + day offset), NOT by week_start: a shift on 1 Jan sits in a
  // week that STARTS in the prior year, so a week_start-only window silently drops
  // it (e.g. New Year's Day PH work never accrued) and over-counts late-December
  // shifts whose week runs into January. When `year` is given we gate strictly on
  // the real date; when it's absent (unit tests) every shift is counted as before.
  const yearStart   = year != null ? (year + '-01-01') : null;
  const yearEndExcl = year != null ? ((parseInt(year, 10) + 1) + '-01-01') : null;
  // Country-scoped PH date sets for fast lookup.
  const phByCountry = {};
  (phRows || []).forEach(p => {
    if (!phByCountry[p.country]) phByCountry[p.country] = new Set();
    phByCountry[p.country].add(p.date);
  });
  // staffId → the country key used to match public holidays (via stores.areas).
  // See phMatchKey: strip the org prefix off area.country ("ELC - UAE" → "UAE")
  // so it matches public_holidays.country ("UAE"); fall back to area.region only
  // when country is absent.
  const staffCountry = {};
  (staffRows || []).forEach(s => {
    staffCountry[s.id] = phMatchKey(s.stores && s.stores.areas);
  });
  // v136.84 (the pay war game, 30 Oct 2026): a shift's OWN store, when it has one, decides its country. A
  // person who moved from the UAE to Qatar in April had every 2026 shift judged against Qatar's holidays,
  // so a UAE holiday worked before the move lost its 8h of TOIL (and a Qatar-only date gained 8h). Shifts
  // carry store_id only when they are not plain home shifts (cover, or pinned to the old store by a move),
  // so for everyone who has not moved or covered across a border this is byte-identical: home shifts have
  // no store_id and keep the person's country, and same-country cover gives the same answer.
  const areasByStore = (staffRows && staffRows.areasByStoreId) || null;
  const countryOfShift = (sh) => {
    if (areasByStore && sh.store_id != null && areasByStore[sh.store_id]) {
      const k = phMatchKey(areasByStore[sh.store_id]);
      if (k) return k;
    }
    return staffCountry[sh.staff_id];
  };
  // v129.154 (split shifts step 3, #1177) — count by staff-DAY, not by ROW.
  //
  // With v198-0's seq, a person can hold TWO legs on one date (28 July 2026:
  // Arabian Ranches 2 09:00-13:00 then a Dubai Mall stocktake 22:00-07:00).
  // The old per-row fold was wrong for that world in three ways:
  //   - a two-leg day flagged for variance TOIL would be charged the 8h
  //     baseline TWICE (each leg's worked - 8), turning 3.5h + 8h into
  //     -4.5 + 0 = -4.5 instead of +3.5;
  //   - a two-leg day on a public holiday would accrue 16h of PH TOIL;
  //   - two leave rows on one date would count as two days.
  //
  // So: aggregate each staff-day first, then fold days into the year counts.
  // A day's variance = (sum of its work legs' worked hours) - ONE 8h baseline,
  // credited when ANY leg carries the manager's variance_toil approval —
  // the manager approves the DAY's total, which is what the modal shows.
  // Every single-leg day computes exactly what the per-row fold computed
  // (same maths, same 0.1h rounding), so history is byte-identical.
  const dayAgg = {};   // staffId -> dateStr -> { hol, sick, toilTaken, work, ph, workedHrs, flagged }
  (shiftRows || []).forEach(sh => {
    const dateStr = shiftDateStr(sh.week_start, sh.day);
    if (yearStart && (dateStr < yearStart || dateStr >= yearEndExcl)) return; // outside the calendar year
    const days = dayAgg[sh.staff_id] || (dayAgg[sh.staff_id] = {});
    const d = days[dateStr] || (days[dateStr] = { hol: false, sick: false, toilTaken: false, toilExplicitHrs: 0, toilWholeDay: false, work: false, ph: false, workedHrs: 0, flagged: false });
    if (sh.type === 'holiday') d.hol = true;
    else if (sh.type === 'sick') d.sick = true;
    else if (sh.type === 'toil') {
      // v132.48 (the ops manager, 22 Sep 2026): a TOIL day may now deduct HOURS.
      // NULL/absent toil_hours means a whole day, which is 8 - exactly what
      // this counted before - so every row written before v132.48, and every
      // balance derived from one, is unchanged BY CONSTRUCTION.
      //
      // A day can carry two legs (seq 1 and 2), so the hours ACCUMULATE
      // across legs while the flag stays a flag. Guarded to the same range
      // as the database CHECK: a junk value must not silently restate a
      // balance, and 0 must not turn a TOIL day into a free one.
      // typeof check first: Number(true) is 1, and a stray boolean must not
      // quietly become a one-hour TOIL day.
      // typeof check first: Number(true) is 1, and a stray boolean must not
      // quietly become a one-hour TOIL day.
      const rawToil = (typeof sh.toil_hours === 'number' || typeof sh.toil_hours === 'string')
        ? Number(sh.toil_hours) : NaN;
      const explicit = (Number.isFinite(rawToil) && rawToil > 0 && rawToil <= 24) ? rawToil : null;
      // A DAY is the unit, and has been since v129.154: two TOIL legs on one
      // date are ONE day taken, not two. So explicit hours ADD UP across the
      // legs (3 + 2 = 5, the manager said so twice), while a leg with no
      // hours is a whole-day marker and pins the day to at least 8. Two
      // legacy legs therefore still cost 8, not 16 - which is exactly what
      // tests/v129-154-per-day-maths_test.js has always required.
      if (explicit == null) d.toilWholeDay = true;
      else d.toilExplicitHrs = (d.toilExplicitHrs || 0) + explicit;
      d.toilTaken = true;
    }
    else if (sh.type === 'work' || sh.type === 'atstore') {
      d.work = true;
      const country = countryOfShift(sh);   // v136.84: the shift's store first, the person's second
      if (country && phByCountry[country] && phByCountry[country].has(dateStr)) d.ph = true;
      // A leg with missing/unparseable times is treated as not there for the
      // variance maths: it adds no hours AND cannot flag the day (else a
      // flagged junk row would charge a phantom -8 baseline — the exact
      // defensive rule shiftVarianceHours always had, kept per-day).
      const worked = shiftWorkedHours(sh);
      if (worked !== null) {
        d.workedHrs += worked;
        // v129.68: work-typed rows only — a flag on a leave row is garbage
        // data and contributes nothing.
        if (sh.variance_toil) d.flagged = true;
      }
    }
  });
  const counts = {};
  Object.keys(dayAgg).forEach(staffId => {
    const c = counts[staffId] = { hol: 0, sick: 0, toilTaken: 0, toilHrsTaken: 0, phWorkAccrued: 0, varianceHrs: 0 };
    Object.keys(dayAgg[staffId]).forEach(dateStr => {
      const d = dayAgg[staffId][dateStr];
      if (d.hol) c.hol += 1;
      if (d.sick) c.sick += 1;
      if (d.toilTaken) {
        c.toilTaken += 1;
        // Resolve the day: explicit hours if any were given, but never less
        // than a whole day when a whole-day leg is present. Capped at 24 so
        // no single date can deduct more than a day exists.
        const ex = d.toilExplicitHrs || 0;
        const cost = d.toilWholeDay ? Math.max(8, ex) : ex;
        c.toilHrsTaken += Math.min(24, cost);
      }
      if (d.ph) c.phWorkAccrued += 1;
      // One baseline per day, matching the modal's per-shift figure exactly
      // when the day has one leg (round once, after the subtraction).
      if (d.flagged) c.varianceHrs += Math.round((d.workedHrs - 8) * 10) / 10;
    });
  });
  // One row per staff, preserving the existing response shape.
  return (balanceRows || []).map(b => {
    const c = counts[b.staff_id] || { hol: 0, sick: 0, toilTaken: 0, toilHrsTaken: 0, phWorkAccrued: 0, varianceHrs: 0 };
    const holiday_total = b.holiday_total || 0;
    const sick_total    = b.sick_total    || 0;
    // v137: an OPTIONAL manual TOIL correction (from the bulk leave-balance
    // reconcile upload) is folded in additively. NULL/absent = 0 = pure rota,
    // so this is behaviour-identical for every staffer without a correction.
    const toil_adjust   = Number(b.toil_adjust) || 0;
    // v129.68: round the SUM too — per-shift values are 0.1-rounded but float
    // addition can still leave 1.4999999 artefacts.
    const toil_variance = Math.round(c.varianceHrs * 10) / 10;
    // v132.48: was `- 8 * c.toilTaken`, i.e. every TOIL day cost a full eight
    // hours whatever was banked. Now it subtracts the hours ACTUALLY booked,
    // and a day with no toil_hours still contributes exactly 8, so this is
    // arithmetically identical for all pre-v132.48 data.
    const toil_hours_used = Math.round(c.toilHrsTaken * 10) / 10;
    // v132.86 (overnight audit): round the TOTAL to 2 dp. Four float parts
    // could leave -4.44e-16 where the answer is 0, which the negative-TOIL
    // email listed as a debt and the rota save warned about. Two places keeps
    // every real value (0.25, 0.5) exactly as it was.
    const toil_balance  = Math.round((8 * c.phWorkAccrued - toil_hours_used + toil_adjust + toil_variance) * 100) / 100;
    return {
      staff_id:     b.staff_id,
      year:         b.year,
      holiday_total,
      holiday_used: c.hol,
      sick_total,
      sick_used:    c.sick,
      toil_balance,
      // v129.36 — the SAME number, decomposed. Matt asked for TOIL to be
      // editable in admin/People/Balances, and editing it means exposing
      // toil_adjust: the mechanism behind the 3 Aug Qatar error, where a
      // correction was applied on top of a fix that had already been made and
      // took 8h off two people. Showing "earned − taken = rota, plus a manual
      // adjustment" makes an editor see WHICH part they are changing, rather
      // than typing a total and hoping.
      //
      // Additive fields only: nothing above changes, so every existing reader
      // (the staff app Balances screen, the admin table, the promotion
      // dossier) is byte-identical. Derived, never stored — toil_balance stays
      // the one figure of record.
      toil_earned_hours: 8 * c.phWorkAccrued,
      // v132.48: was 8 * c.toilTaken. It MUST be the same figure the balance
      // subtracts, or this decomposition stops adding up to toil_balance -
      // which is the one thing it exists to do.
      toil_taken_hours:  toil_hours_used,
      toil_adjust,
      // v129.68: the fourth term of the decomposition — approved shift-variance
      // TOIL, computed from flagged rota rows. Additive field: existing readers
      // that ignore it still see the correct toil_balance.
      toil_variance_hours: toil_variance,
      updated_at:   b.updated_at,
    };
  });
}
